<template>
  <div v-bind:class="{'upload-page': true, 'has-uploads': uploads.length}">
    <div v-if="uploads.length > 0" class="text-center pt-3 pt-md-4">
      <button class="btn btn-primary btn-lg" v-on:click="uploadFromFile">
        <i class="fas fa-upload"></i>
        {{ t.upload_image }}
      </button>
      <div style="font-size: 90%;" class="mt-2 mb-3 mb-md-4">
        {{ t.drop_paste_text_start }}
        <a href="#" v-on:click.prevent="uploadFromUrl" class="text-dark" style="text-decoration: underline;">{{ t.drop_paste_text_url }}</a>
        {{ t.drop_paste_text_end }}
      </div>
      <hr>
    </div>
    <div class="container">
      <upload-list v-bind:uploads="uploads" v-if="uploads.length > 0" v-on:delete="deleteImage($event.key)" ref="uploadlist" v-bind:t="t" v-bind:fullImageId="fullImageId" />
      <div class="empty-state" v-if="uploads.length == 0">
        <h1>
          <span v-html="t.upload_page_heading_html"></span>
        </h1>
        <div style="max-width: 350px;" class="mx-auto mt-5">
          <upload-widget v-on:uploadFile="uploadFromFile" v-on:uploadUrl="uploadFromUrl" v-bind:t="t" />
        </div>
        <div class="mt-5 d-flex justify-content-center">
          <div class="d-flex">
            <div class="font-weight-bold text-muted mr-2 text-left" style="font-size: 80%">
              <div>{{ t.no_image }}</div>
              <div>{{ t.try_one_of_these }}</div>
            </div>
            <div style="min-width: 176px;">
              <example-images v-on:picked="examplePicked" theme="small" />
            </div>
          </div>
        </div>
        <div style="font-size: 60%; opacity: 0.5; max-width: 385px;" class="mt-4 text-center mx-auto">
          <span v-html="t.upload_terms_html"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UploadList from './upload_list';
import UploadWidget from './upload_widget';
import ExampleImages from './example_images';

import { ImageResizer } from '@remove-bg/remove-bg-tools';

import { pickFile } from '../src/pick_file';
import { hcaptcha_invisible } from '../src/hcaptcha';

import { Base64 } from 'js-base64';

const maxConcurrent = 2;
const maxSize = 1024*1024*12;

export default {
  props: {
    t: Object,
    fullImageId: String,
  },
  components: { UploadList, UploadWidget, ExampleImages },
  mounted() {
    this.fetchExistingUploads();
  },
  data() {
    return {
      uploads: [],
    };
  },
  computed: {
    processingCount() {
      return this.uploads.filter((upload) => upload.status != 'queued' && upload.status != 'error' && upload.status != 'finished').length;
    }
  },
  methods: {
    uploadFromUrl() {
      var url = prompt(`${this.t.image_url}:`);
      window.track("Images", "upload_enter_url", "Enter URL");
      this.urlPicked(url);
    },
    urlPicked(url) {
      if(url == null || url == "") return;
      if(!url.match(/^https?:\/\/.+$/)) {
        alert(this.t.invalid_image_url);
        return;
      }
      this.enqueue({url});
    },
    examplePicked(exampleImage) {
      window.track("Images", "upload_sample_image", "Sample Image");
      this.urlPicked(exampleImage.url);
    },
    uploadFromFile() {
      pickFile().then((file) => {
        window.track("Images", "upload_select_file", "Select file");
        this.filePicked(file);
      });
    },
    filePickedBase64(filename, mimetype, base64) {
      var sliceSize = 512;
      const byteCharacters = atob(base64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, {type: mimetype});
      
      this.filePicked({ file: blob, size: blob.size, type: mimetype, filename: filename }, true);
    },
    filePicked(file, isBlob = false) {
      if(file.type != "image/jpeg" && file.type != "image/png") {
        alert(this.t.only_jpg_png_supported);
        return;
      }

      if(file.size <= maxSize) {
        if(isBlob) {
          this.enqueue({ file: file.file, filename: file.filename });
        }
        else {
          this.enqueue({ file });
        }
      }
      else {
        var imageResizer = new ImageResizer(file);
        if (imageResizer.supports(file)) {
          imageResizer.reduce().then(returnObj => {
            if(blob.size > maxSize) {
              alert(this.t.max_file_size_exceeded);
              return;
            }
            this.enqueue({ file: returnObj.blob, filename: file.name, uploadingNote: this.t.image_resized });
          }).catch((error) => {
            if(window.error_tracking) window.error_tracking.sendError(error);
            alert(this.t.max_file_size_exceeded);
          });
        }
        else {
          alert(this.t.max_file_size_exceeded);
        }
      }
    },
    enqueue(upload) {
      this.uploads.unshift({
        key: this.generateKey(),
        status: 'queued', // queued -> (token_requested (-> captcha_required) -> uploading -> processing -> finished) || error
        errorMessage: "",
        canSkipCaptcha: null,
        creditBalance: null,
        uploadProgress: 0.0,
        remote: {},
        ...upload
      });
      this.processQueue();

      this.$nextTick(() => {
        var uploadlist = this.$refs['uploadlist'];
        if(!uploadlist) return;
        $('html, body').animate({
          scrollTop: $(uploadlist.$el).offset().top - 90
        }, 300);
      })
    },
    processQueue() {
      if(this.uploads.length == 0 || this.processingCount >= maxConcurrent) return;
      var oldest = this.uploads.find((upload) => upload.status == 'queued');
      if(!oldest) return;
      this.process(oldest);
    },
    process(upload) {
      upload.status = 'token_requested';

      this.requestToken(upload);
    },
    requestToken(upload, tryCount=1) {
      $.ajax({
        type: 'POST',
        url: this.t.trust_tokens_url,
        data: upload.trustTokenData,
        success: (data) => {
          var useToken = (token) => {
            upload.token = token;
            this.doUpload(upload);
          }
          if(data.request && typeof(data.request) == "string") {
            eval(data.request);
          }
        },
        error: (xhr) => {
          var submitChallenge = (upload, value) => {
            upload.trustTokenData = value;
            this.requestToken(upload);
          }

          var request_hcaptcha_checkbox = (config, upload, data) => {
            upload.status = "captcha_required";
            upload.captchaConfig = {
              type: "hcaptcha_checkbox",
              config: config,
            }
            upload.canSkipCaptcha = data.can_skip_captcha;
            upload.creditBalance = data.credit_balance;
            upload.onCaptchaCompleted = (captcha) => {
              upload.status = "token_requested";
              submitChallenge(upload, {
                "proof": "hcaptcha_checkbox",
                "hcaptcha_response": captcha.value,
              });
            };
          }

          var request_hcaptcha_invisible = (config, upload, data) => {
            hcaptcha_invisible(config).then((captcha) => {
              submitChallenge(upload, {
                "proof": "hcaptcha_invisible",
                "hcaptcha_response": captcha,
              });
            }).catch((e) => {
              upload.status = "error";
              upload.errorMessage = e.message;
              this.processQueue();
            });
          }

          if(xhr.status == 422 && xhr.responseJSON && xhr.responseJSON.request && typeof(xhr.responseJSON.request) == "string") {
            eval(xhr.responseJSON.request);
          }
          else if(xhr.status == 422 && xhr.responseJSON && xhr.responseJSON.status == "invalid_csrf_token" && tryCount < 3) {
            $("meta[name='csrf-token']").attr("content", xhr.responseJSON.csrf_token);
            this.requestToken(upload, tryCount + 1)
          } else {
            upload.status = "error";
            upload.errorMessage = `Error: Failed to prepare upload (error code ${xhr.status}).\nSorry – please reload the page and try again.\nIf the problem persists, contact team@remove.bg`;
            if(window.error_tracking) {
              window.error_tracking.sendError(new Error(`Upload token request failed (try ${tryCount}): Status ${xhr.status}, Text ${(xhr.responseText ? xhr.responseText : "").substring(0,100)}`));
            }
          }
        }
      })
    },
    doUpload(upload) {
      upload.status = "uploading";
      upload.uploadProgress = 0.0;

      var formdata = new FormData();
      formdata.append('trust_token', upload.token);
      if(upload.file) {
        if(upload.filename) {
          formdata.append('image[original]', upload.file, upload.filename);
        }
        else {
          formdata.append('image[original]', upload.file);
        }
      }
      else if(upload.url) {
        formdata.append('image[original_source_url]', upload.url);
      }

      $.ajax({
        url: this.t.images_url,
        type: 'POST',
        data: formdata,
        cache: false,
        contentType: false,
        processData: false,
        xhr: () => {
          var myXhr = $.ajaxSettings.xhr()
          if(myXhr.upload) {
            myXhr.upload.addEventListener('progress', (e) => {
              if(e.lengthComputable && e.total > 0) {
                upload.uploadProgress = e.loaded / e.total;
              }
            }, false)
          }
          return myXhr;
        },
        success: (data) => {
          upload.remote = this.formatRemote(data);
          this.showResult(upload);
        },
        error: (xhr) => {
          upload.status = "error";
          upload.errorMessage = `Image Upload failed (${xhr.status}): Please try again later or select a different image.`;
          this.processQueue();
        }
      });
    },
    showResult(upload) {
      if(upload.remote.fetch_in) {
        upload.status = "processing";
        setTimeout(() => {
          $.ajax({
            type: 'GET',
            url: upload.remote.url,
            cache: false,
            success: (data) => {
              upload.remote = this.formatRemote(data);
              this.showResult(upload);
            },
            error: (xhr) => {
              upload.status = "error";
              upload.errorMessage = `Image Upload failed (${xhr.status}): Please try again later or select a different image.`;
            }
          });
        }, upload.remote.fetch_in);
      }
      else {
        upload.status = "finished";
        this.processQueue();
      }
    },
    generateKey() {
      return `${new Date().getTime()}-${Math.round(1000000000*Math.random())}`;
    },
    deleteImage(key) {
      var idx = this.uploads.findIndex((upload) => upload.key == key);
      if(idx == -1) return;
      this.uploads.splice(idx, 1);
    },
    fetchExistingUploads() {
      $.ajax({
        url: this.t.images_url,
        type: "GET",
        success: (data) => {
          for(var i = 0; i < data.data.length; i++) {
            var upload = {
              key: data.data[i].id,
              remote: this.formatRemote(data.data[i]),
            };
            this.showResult(upload);
            this.uploads.push(upload);
          }
        },
      });
    },
    formatRemote(remote) {
      var pl = remote.pl;
      if(pl) {
        delete remote.pl;
        // We're using Base64 (https://www.npmjs.com/package/js-base64) here, because
        // "atob" does not properly decode multi-byte characters, such as Japanese zenkaku
        var plParsed = JSON.parse(Base64.decode(pl));
        remote = { ...remote, ...plParsed };
      }
      return remote;
    }
  }
}
</script>

<style scoped>
.upload-page {
  padding-bottom: 40px;
  transition: background 300ms;
}
.upload-page.has-uploads {
  background: #F7F9FA;
}
.empty-state {
  text-align: center;
  padding: 60px 0;
}
@media(min-height: 800px) { .empty-state { padding: 100px 0 100px 0; } }
@media(min-height: 900px) { .empty-state { padding: 100px 0 170px 0; } }
@media(min-height: 1000px) { .empty-state { padding: 100px 0 220px 0; } }
</style>
